import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { subscriptionRepository } from "../repositories/subscription.repository";
import type {
  FeatureLimitCheckResponse,
  NumericFeatureKey,
} from "../types/subscription.types";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { getSubscriptionLimitsEnabled } from "./useEnhancedSubscriptionFeatures";

const ENABLE_SUBSCRIPTION_LIMITS = getSubscriptionLimitsEnabled();

/**
 * Query key helper for feature limit checks
 * Supports both terreiro and medium (personal) contexts
 */
export const checkFeatureLimitQueryKey = (
  feature: NumericFeatureKey,
  context?: { terreiroId?: string; mediumId?: string },
) => {
  const id = context?.terreiroId ?? context?.mediumId ?? "no-context";
  const type = context?.terreiroId
    ? "terreiro"
    : context?.mediumId
      ? "medium"
      : "unknown";
  return ["subscription", type, id, "check-limit", feature] as const;
};

interface UseCheckFeatureLimitQueryProps {
  /** The feature to check the limit for */
  feature: NumericFeatureKey;
  /** Override the terreiro ID (defaults to cached terreiro) */
  terreiroId?: string;
  /** Override the medium ID (defaults to cached medium) - for personal subscriptions */
  mediumId?: string;
  /** Current count if already known (avoids extra DB query on backend) */
  currentCount?: number;
  /** Enable/disable the query */
  enabled?: boolean;
}

/**
 * Hook to check if a feature limit has been reached
 *
 * Supports both terreiro and personal (medium) subscription contexts.
 * Automatically detects context from cached data if not explicitly provided.
 *
 * Use this hook before showing create buttons/forms to determine if
 * the user can create more of a resource. Returns detailed information
 * about current usage, remaining quota, and upgrade suggestions.
 *
 * @example
 * ```tsx
 * // Auto-detect context from cache
 * const { data: limitCheck, isLoading } = useCheckFeatureLimitQuery({
 *   feature: 'maxEvents',
 * });
 *
 * // Explicit terreiro context
 * const { data: limitCheck } = useCheckFeatureLimitQuery({
 *   feature: 'maxEvents',
 *   terreiroId: 'terreiro-123',
 * });
 *
 * // Explicit medium context (personal subscription)
 * const { data: limitCheck } = useCheckFeatureLimitQuery({
 *   feature: 'maxEvents',
 *   mediumId: 'medium-456',
 * });
 *
 * if (!limitCheck?.allowed) {
 *   // Show upgrade prompt
 * }
 *
 * if (limitCheck?.nearingLimit) {
 *   // Show warning about approaching limit
 * }
 * ```
 */
export function useCheckFeatureLimitQuery({
  feature,
  terreiroId: overrideTerreiroId,
  mediumId: overrideMediumId,
  currentCount,
  enabled = true,
}: UseCheckFeatureLimitQueryProps) {
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();

  // Determine context: use overrides first, then cached values
  const terreiroId = overrideTerreiroId ?? cachedTerreiro?.id;
  const mediumId = overrideMediumId ?? cachedMedium?.id;

  // Prioritize terreiro context if both exist (terreiro subscriptions take precedence)
  const hasTerreiro = !!terreiroId;
  const hasMedium = !hasTerreiro && !!mediumId;

  // Build context object for query key and API call
  const context = hasTerreiro
    ? { terreiroId }
    : hasMedium
      ? { mediumId }
      : undefined;

  // Enable query only when:
  // 1. Feature flag is on
  // 2. Feature is specified
  // 3. We have a valid context (terreiro OR medium)
  // 4. enabled prop is true
  const shouldEnableQuery =
    enabled && !!feature && ENABLE_SUBSCRIPTION_LIMITS && !!context;

  return useQuery<FeatureLimitCheckResponse, Error>({
    queryKey: checkFeatureLimitQueryKey(feature, context),
    queryFn: () => {
      // Backend API will determine access based on active subscription context
      if (hasTerreiro) {
        return subscriptionRepository.checkLimit(
          feature,
          terreiroId,
          undefined,
          currentCount,
        );
      } else if (hasMedium) {
        // For personal subscriptions, mediumId is passed as the third parameter
        return subscriptionRepository.checkLimit(
          feature,
          undefined,
          mediumId,
          currentCount,
        );
      }
      throw new Error("No valid subscription context");
    },
    staleTime: 1000 * 60 * 2, // 2 minutes (shorter than features, more dynamic)
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: "always",
    enabled: shouldEnableQuery,
    retry: (failureCount, error) => {
      // Don't retry on 404 - API not implemented yet
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },

    retryDelay: (attemptIndex) => {
      return Math.min(1000 * 2 ** attemptIndex, 4000);
    },

    // Don't refetch on window focus for limit checks (avoid flicker)
    refetchOnWindowFocus: false,

    meta: {
      errorMessage: "Failed to check feature limit",
    },
  });
}

/**
 * Helper to invalidate limit check queries after resource creation/deletion
 *
 * @example
 * ```tsx
 * const queryClient = useQueryClient();
 *
 * // After creating an event in terreiro context
 * queryClient.invalidateQueries({
 *   queryKey: checkFeatureLimitQueryKey('maxEvents', { terreiroId }),
 * });
 *
 * // After creating an event in personal context
 * queryClient.invalidateQueries({
 *   queryKey: checkFeatureLimitQueryKey('maxEvents', { mediumId }),
 * });
 * ```
 */
export { checkFeatureLimitQueryKey as invalidateFeatureLimitQueryKey };
